///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { LoadingManager, LoaderUtils } from "three";

export type GLTFFileSource = string | globalThis.File | ArrayBuffer;

export type GLTFLoadParams = {
  path?: string;
  externalFiles?: Map<string, GLTFFileSource>;
  crossOrigin?: string;
  requestHeader?: HeadersInit;
  withCredentials?: boolean;
};

export class GLTFLoadingManager extends LoadingManager {
  public path = "";
  public resourcePath = "";
  public fileURL = "";
  public dataURLs = new Map();

  constructor(file: GLTFFileSource, params: GLTFLoadParams = {}) {
    super();

    this.path = params.path || "";

    const externalFiles = params.externalFiles || new Map();

    if (typeof file === "string") {
      this.fileURL = file;
      this.resourcePath = LoaderUtils.extractUrlBase(file);
    } else {
      externalFiles.forEach((value, key) => (this.fileURL = value === file ? key : this.fileURL));
      externalFiles.set(this.fileURL, file);
    }

    externalFiles.forEach((value, key) => {
      let dataURL: string;
      if (typeof value === "string") dataURL = value;
      else dataURL = URL.createObjectURL(new Blob([value]));
      this.dataURLs.set(key, dataURL);
    });

    this.setURLModifier((url: string) => {
      const key = decodeURI(url)
        .replace(this.path, "")
        .replace(this.resourcePath, "")
        .replace(/^(\.?\/)/, "");
      const dataURL = this.dataURLs.get(key);
      return dataURL ?? url;
    });
  }

  dispose() {
    this.dataURLs.forEach(URL.revokeObjectURL);
  }
}
